import type { AcceptableInputValue } from 'reka-ui'
import { computed, type Ref, ref } from 'vue'

const DOMAIN_REGEX = /^[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9](?:\.[a-zA-Z]{2,})+$/

export function useDomainValidation(domainsModel: Ref<string[]>) {
  const immediateErrorMessage = ref('')
  const persistedInvalidTextForPlaceholder = ref('')

  // Computed property for display and consistent lowercase state
  const displayDomains = computed<string[]>(() => {
    if (Array.isArray(domainsModel.value)) {
      return domainsModel.value.map((d) => d.toLowerCase())
    }

    return []
  })

  const handleDomainUpdate = (newValuesRaw: AcceptableInputValue[]) => {
    const convertToDomainStringsAndLowercase = (values: AcceptableInputValue[]): string[] => {
      return values.map((val) => (typeof val === 'string' ? val.toLowerCase() : val.text.toLowerCase()))
    }

    // Preserve original casing of submitted texts for potential placeholder use
    const rawSubmittedTextsOriginalCase = newValuesRaw.map((val) => (typeof val === 'string' ? val : val.text))
    const allSubmittedItemsLowercase = convertToDomainStringsAndLowercase(newValuesRaw)
    const actualCurrentTagsLowercase = displayDomains.value // Already lowercase and reactive

    const newlySubmittedTextsLowercase = allSubmittedItemsLowercase.filter((s) => !actualCurrentTagsLowercase.includes(s))

    let finalEffectiveTagsLowercase: string[]
    let newErrorMessage = ''
    let newTextForPlaceholder = ''

    if (newlySubmittedTextsLowercase.length > 0) {
      const validNewSubmissionsLowercase = newlySubmittedTextsLowercase.filter((t) => DOMAIN_REGEX.test(t))
      const invalidNewSubmissionsLowercase = newlySubmittedTextsLowercase.filter((t) => !DOMAIN_REGEX.test(t))

      if (invalidNewSubmissionsLowercase.length > 0) {
        newErrorMessage = 'Invalid domain format. Example: domain.com'

        const placeholderTexts: string[] = []

        rawSubmittedTextsOriginalCase.forEach((originalText) => {
          if (invalidNewSubmissionsLowercase.includes(originalText.toLowerCase())) {
            placeholderTexts.push(originalText)
          }
        })
        newTextForPlaceholder = placeholderTexts.join(', ')
      }

      finalEffectiveTagsLowercase = actualCurrentTagsLowercase
        .filter((tag) => allSubmittedItemsLowercase.includes(tag))
        .concat(validNewSubmissionsLowercase)
    } else {
      finalEffectiveTagsLowercase = allSubmittedItemsLowercase
    }

    immediateErrorMessage.value = newErrorMessage
    persistedInvalidTextForPlaceholder.value = newTextForPlaceholder

    // Update the source ref with unique, lowercase domains
    domainsModel.value = [...new Set(finalEffectiveTagsLowercase)]
  }

  return {
    immediateErrorMessage,
    persistedInvalidTextForPlaceholder,
    handleDomainUpdate,
    displayDomains, // Use this for :model-value in TagsInput and v-for
  }
}
